/**
 * PX-0 — the single canonical, versioned growth event contract.
 *
 * Before this module, the homepage emitted `hero_view` / `page_view` /
 * `quote_start` while the founder growth dashboard counted `homepage_view` /
 * `quote_started` / `proof_card_click`, so conversion reporting read zero.
 * There is exactly ONE declaration of funnel truth: this file. Producers
 * (pages) and consumers (dashboards) both import from here, and the contract
 * test fails the build if a critical funnel event is emitted or consumed
 * without being declared.
 *
 * This does NOT replace the growth transport in `@/lib/growth` — it is the
 * contract layer above the existing batched writer.
 */
import { trackGrowth } from "@/lib/growth";

export const EVENT_CONTRACT_VERSION = "1.0.0";

export type GrowthStage =
  | "acquisition"
  | "activation"
  | "conversion"
  | "retention"
  | "referral";

export interface GrowthEventDef {
  /** Wire name written to growth_events.event_name. Never rename without a version bump. */
  name: string;
  stage: GrowthStage;
  /** Critical funnel events must exist for the funnel report to be meaningful. */
  funnel_critical: boolean;
  /** Metadata keys the emitter must supply. */
  required: readonly string[];
  purpose: string;
}

export const GROWTH_EVENTS = [
  {
    name: "homepage_view",
    stage: "acquisition",
    funnel_critical: true,
    required: ["path"],
    purpose: "A visitor rendered the public homepage.",
  },
  {
    name: "hero_intent_selected",
    stage: "acquisition",
    funnel_critical: false,
    required: ["intent"],
    purpose: "Visitor chose the ship or haul side of the dual-intent hero.",
  },
  {
    name: "proof_card_click",
    stage: "acquisition",
    funnel_critical: true,
    required: ["source"],
    purpose: "Visitor engaged a trust/proof surface on the homepage.",
  },
  {
    name: "quote_started",
    stage: "conversion",
    funnel_critical: true,
    required: ["source"],
    purpose: "Shipper submitted the homepage quote-start form.",
  },
  {
    name: "signup_started",
    stage: "conversion",
    funnel_critical: true,
    required: ["source", "role"],
    purpose: "A CTA sent the visitor to account creation with a role intent.",
  },
  {
    name: "signup_completed",
    stage: "conversion",
    funnel_critical: true,
    required: ["role"],
    purpose: "Account credentials were created.",
  },
  {
    name: "role_selected",
    stage: "activation",
    funnel_critical: true,
    required: ["role", "prefilled"],
    purpose: "Role chosen in onboarding; prefilled records whether intent survived.",
  },
  {
    name: "onboarding_completed",
    stage: "activation",
    funnel_critical: true,
    required: ["role"],
    purpose: "Identity provisioned through the central RPC.",
  },
  {
    name: "workspace_entered",
    stage: "activation",
    funnel_critical: true,
    required: ["role", "tier"],
    purpose: "User reached a role workspace (limited or verified).",
  },
  {
    name: "draft_resumed",
    stage: "activation",
    funnel_critical: true,
    required: ["source"],
    purpose: "A pre-auth shipper draft was restored after authentication.",
  },
  {
    name: "booking_started",
    stage: "conversion",
    funnel_critical: false,
    required: [],
    purpose: "Booking wizard opened.",
  },
  {
    name: "booking_submitted",
    stage: "conversion",
    funnel_critical: true,
    required: [],
    purpose: "Authoritative booking command accepted.",
  },
  {
    name: "transporter_join_started",
    stage: "conversion",
    funnel_critical: true,
    required: ["source"],
    purpose: "Supply-side CTA engaged.",
  },
  {
    name: "verification_step_completed",
    stage: "activation",
    funnel_critical: false,
    required: ["step"],
    purpose: "A trust-centre verification step was completed.",
  },
  {
    name: "referral_invite_sent",
    stage: "referral",
    funnel_critical: false,
    required: ["channel"],
    purpose: "A user shared an invite (PX-8).",
  },
  {
    name: "referral_signup",
    stage: "referral",
    funnel_critical: false,
    required: [],
    purpose: "A signup carried a referral code (PX-8).",
  },
  {
    name: "account_created",
    stage: "conversion",
    funnel_critical: true,
    required: [],
    purpose: "Signup returned without error (role only, no identity).",
  },
  {
    name: "onboarding_role_confirmed",
    stage: "activation",
    funnel_critical: true,
    required: ["role"],
    purpose: "Role provisioning accepted by the server.",
  },
  {
    name: "booking_draft_started",
    stage: "conversion",
    funnel_critical: true,
    required: [],
    purpose: "Booking wizard opened with an unknown fact set.",
  },
  {
    name: "booking_step_reached",
    stage: "conversion",
    funnel_critical: true,
    required: ["step"],
    purpose: "Booking wizard advanced to a step — drop-off measurement.",
  },
  {
    name: "cargo_proposal_accepted",
    stage: "conversion",
    funnel_critical: true,
    required: ["field"],
    purpose: "User applied a Smart Cargo Capture proposal.",
  },
  {
    name: "quote_requested",
    stage: "conversion",
    funnel_critical: true,
    required: [],
    purpose: "Authoritative CBR price requested for a real route.",
  },
  {
    name: "quote_authoritative_ready",
    stage: "conversion",
    funnel_critical: true,
    required: [],
    purpose: "Server CBR price returned for a real route.",
  },
  {
    name: "booking_confirmed",
    stage: "conversion",
    funnel_critical: true,
    required: [],
    purpose: "create_booking command succeeded.",
  },
  {
    name: "offer_presented",
    stage: "conversion",
    funnel_critical: true,
    required: ["count"],
    purpose: "Pending offers shown to a transporter.",
  },
  {
    name: "offer_accepted",
    stage: "conversion",
    funnel_critical: true,
    required: [],
    purpose: "accept_carrier_offer command succeeded.",
  },
  {
    name: "payment_checkout_opened",
    stage: "conversion",
    funnel_critical: true,
    required: [],
    purpose: "Paystack checkout session created by the server.",
  },
  {
    name: "payment_confirmed",
    stage: "conversion",
    funnel_critical: true,
    required: [],
    purpose: "Server balance shows the load fully paid (provider-confirmed).",
  },
  { name: "verification_document_uploaded", stage: "activation", funnel_critical: true, required: ["subject"], purpose: "A verification document was stored privately." },
  { name: "verification_submitted", stage: "activation", funnel_critical: true, required: ["subject"], purpose: "An entity, vehicle or driver was submitted for review." },
  // Three-step booking journey. Correlated by draft_id; never free text or contact details.
  { name: "booking_step_viewed", stage: "conversion", funnel_critical: true, required: ["draft_id", "step"], purpose: "A booking step was shown." },
  { name: "booking_step_completed", stage: "conversion", funnel_critical: true, required: ["draft_id", "step"], purpose: "A booking step passed validation." },
  { name: "booking_validation_failed", stage: "conversion", funnel_critical: false, required: ["draft_id", "step"], purpose: "A booking step was blocked by missing or invalid fields." },
  { name: "booking_draft_resumed", stage: "conversion", funnel_critical: false, required: ["draft_id"], purpose: "A saved booking draft was resumed." },
  { name: "booking_route_resolved", stage: "conversion", funnel_critical: false, required: ["draft_id"], purpose: "An authoritative road route was calculated for the draft." },
  { name: "booking_quote_shown", stage: "conversion", funnel_critical: true, required: ["draft_id"], purpose: "A firm price or a quote-request path was shown." },
  { name: "booking_quote_request_submitted", stage: "conversion", funnel_critical: true, required: ["draft_id"], purpose: "A manual quote request was recorded." },
  { name: "booking_provider_failed", stage: "conversion", funnel_critical: false, required: ["draft_id", "provider"], purpose: "Maps or pricing failed during booking." },
] as const satisfies readonly GrowthEventDef[];

export type GrowthEventName = (typeof GROWTH_EVENTS)[number]["name"];

export const GROWTH_EVENT_NAMES: readonly string[] = GROWTH_EVENTS.map((e) => e.name);

export const FUNNEL_CRITICAL_EVENTS: readonly string[] = GROWTH_EVENTS.filter(
  (e) => e.funnel_critical,
).map((e) => e.name);

export function growthEventDef(name: string): GrowthEventDef | undefined {
  return GROWTH_EVENTS.find((e) => e.name === name);
}

export function isDeclaredGrowthEvent(name: string): boolean {
  return GROWTH_EVENT_NAMES.includes(name);
}

/**
 * Emit a contract event. The envelope (contract version + stage) travels in
 * metadata so a later contract revision stays readable alongside old rows.
 * Telemetry never throws in production — an undeclared name is dropped with a
 * console warning rather than breaking a user journey.
 */
export function trackGrowthEvent(
  name: GrowthEventName,
  payload: Record<string, unknown> = {},
): void {
  const def = growthEventDef(name);
  if (!def) {
    if (import.meta.env.DEV) console.warn(`[growth] undeclared event "${name}"`);
    return;
  }
  trackGrowth(name, {
    ...payload,
    contract_version: EVENT_CONTRACT_VERSION,
    stage: def.stage,
  });
}
